import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const SessionManager = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const navigate = useNavigate();

  const SESSION_TIMEOUT = 30 * 60 * 1000;
  const WARNING_TIME = 25 * 60 * 1000;

  const handleLogout = useCallback(() => {
    setShowWarning(false);
    sessionStorage.removeItem('sessionExpiry');
  }, []);

  const resetTimer = useCallback(() => {
    setShowWarning(false);
    const newExpiryTime = Date.now() + SESSION_TIMEOUT;
    sessionStorage.setItem('sessionExpiry', newExpiryTime?.toString());
  }, [SESSION_TIMEOUT]);

  useEffect(() => {
    const initSession = () => {
      const existingExpiry = sessionStorage.getItem('sessionExpiry');
      if (!existingExpiry) {
        resetTimer();
      }
    };

    initSession();

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events?.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    return () => {
      events?.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [resetTimer]);

  useEffect(() => {
    const checkSession = setInterval(() => {
      const expiryTime = parseInt(sessionStorage.getItem('sessionExpiry') || '0');
      const now = Date.now();
      const remaining = expiryTime - now;

      if (remaining <= 0) {
        handleLogout();
      } else if (remaining <= (SESSION_TIMEOUT - WARNING_TIME)) {
        setShowWarning(true);
        setTimeRemaining(Math.ceil(remaining / 1000));
      }
    }, 1000);

    return () => clearInterval(checkSession);
  }, [SESSION_TIMEOUT, WARNING_TIME, handleLogout]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 z-[1100] bg-background flex items-center justify-center">
      <div className="bg-surface rounded-[14px] p-8 max-w-md w-full mx-4 shadow-warm-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-12 w-12 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="AlertTriangle" size={24} color="var(--color-warning)" />
          </div>
          <h2 className="text-xl font-semibold text-foreground font-heading">Session Expiring Soon</h2>
        </div>

        <p className="text-muted-foreground mb-6">
          Your session will expire in <span className="font-semibold text-warning font-data">{formatTime(timeRemaining)}</span> due to inactivity. 
          Click "Stay Logged In" to continue working or you will be automatically logged out.
        </p>

        <div className="flex gap-3">
          <Button
            variant="outline"
            fullWidth
            onClick={handleLogout}
          >
            Logout Now
          </Button>
          <Button
            variant="default"
            fullWidth
            iconName="RefreshCw"
            iconPosition="left"
            onClick={resetTimer}
          >
            Stay Logged In
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SessionManager;